/**
 * CountryCodeSelect.js
 *
 * A searchable country-code picker for BasicInfoStep's mobile field.
 * Swapped in for a plain <select> because a native dropdown with 200+
 * options is genuinely hard to use — no search, a giant unstyled native
 * popover, and on most platforms no visible flag/dial-code formatting
 * while it's open. This follows the WAI-ARIA combobox pattern: a button
 * that opens a panel with a search input (auto-focused) and a listbox
 * below it, filtered as you type by country name, dial code, or ISO code.
 *
 * Fully keyboard-operable:
 *  - Enter/Space/ArrowDown on the closed trigger opens the panel.
 *  - Typing in the search box filters the list live.
 *  - ArrowDown/ArrowUp move the active option; Enter selects it.
 *  - Escape closes the panel and returns focus to the trigger.
 *  - Clicking outside the panel closes it without changing the value.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ComboBoxWrap,
  ComboBoxTrigger,
  ComboBoxFlag,
  ComboBoxDial,
  ComboBoxChevron,
  ComboBoxPanel,
  ComboBoxSearchWrap,
  ComboBoxSearchIcon,
  ComboBoxSearchInput,
  ComboBoxList,
  ComboBoxOption,
  ComboBoxOptionFlag,
  ComboBoxOptionName,
  ComboBoxOptionDial,
  ComboBoxEmpty,
} from "./BasicInfoStep.styles";

/**
 * @param {object}   props
 * @param {string}   props.id            - Id for the trigger button (label's htmlFor target).
 * @param {Array}    props.countries     - COUNTRY_CODES-shaped list: { code, dial, label, flag }.
 * @param {string}   props.value         - Currently selected country's `code` (ISO alpha-2).
 * @param {function} props.onChange      - Called with the newly selected country's `code`.
 * @param {boolean}  [props.error]       - Shows the field in its error state.
 * @param {string}   [props.ariaLabel]   - Accessible name for the trigger (no visible <Label> targets it directly).
 */
const CountryCodeSelect = ({ id, countries, value, onChange, error, ariaLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = useMemo(
    () => countries.find((c) => c.code === value) ?? countries[0],
    [countries, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase() === q
    );
  }, [countries, query]);

  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    // Land the active option on whatever's currently selected, so arrow
    // keys continue from there instead of jumping back to the top.
    const idx = countries.findIndex((c) => c.code === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [countries, value]);

  const close = useCallback((returnFocus) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (country) => {
      if (country) onChange?.(country.code);
      close(true);
    },
    [onChange, close]
  );

  // Focus the search input the moment the panel opens.
  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  // Keep the active option scrolled into view as it changes.
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const activeEl = listRef.current.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeIndex]);

  // Click-outside-to-close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, close]);

  // Filtering can shrink the list out from under the active index.
  useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(0);
  }, [filtered.length, activeIndex]);

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      open();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  const listboxId = `${id}-listbox`;
  const activeOptionId = filtered[activeIndex] ? `${id}-option-${filtered[activeIndex].code}` : undefined;

  return (
    <ComboBoxWrap ref={wrapRef}>
      <ComboBoxTrigger
        ref={triggerRef}
        id={id}
        type="button"
        $error={!!error}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? close(false) : open())}
        onKeyDown={handleTriggerKeyDown}
      >
        <ComboBoxFlag aria-hidden="true">{selected?.flag}</ComboBoxFlag>
        <ComboBoxDial>{selected?.dial}</ComboBoxDial>
        <ComboBoxChevron aria-hidden="true" $open={isOpen}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ComboBoxChevron>
      </ComboBoxTrigger>

      {isOpen && (
        <ComboBoxPanel role="presentation">
          <ComboBoxSearchWrap>
            <ComboBoxSearchIcon aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="6" cy="6" r="4.75" stroke="currentColor" strokeWidth="1.4" />
                <path d="M12.5 12.5L9.6 9.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </ComboBoxSearchIcon>
            <ComboBoxSearchInput
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              placeholder="Search country or code"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </ComboBoxSearchWrap>

          {filtered.length > 0 ? (
            <ComboBoxList id={listboxId} role="listbox" ref={listRef} aria-label="Countries">
              {filtered.map((c, i) => {
                const isActive = i === activeIndex;
                const isSelected = c.code === value;
                return (
                  <ComboBoxOption
                    key={c.code}
                    id={`${id}-option-${c.code}`}
                    role="option"
                    aria-selected={isSelected}
                    data-active={isActive}
                    $active={isActive}
                    $selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      // mousedown (not click) so this fires before the
                      // input's onBlur/close-on-outside-click logic does.
                      e.preventDefault();
                      commit(c);
                    }}
                  >
                    <ComboBoxOptionFlag aria-hidden="true">{c.flag}</ComboBoxOptionFlag>
                    <ComboBoxOptionName>{c.label}</ComboBoxOptionName>
                    <ComboBoxOptionDial>{c.dial}</ComboBoxOptionDial>
                  </ComboBoxOption>
                );
              })}
            </ComboBoxList>
          ) : (
            <ComboBoxEmpty>No countries match "{query}"</ComboBoxEmpty>
          )}
        </ComboBoxPanel>
      )}
    </ComboBoxWrap>
  );
};

CountryCodeSelect.displayName = "CountryCodeSelect";
export default React.memo(CountryCodeSelect);
